import { useState } from "react";
import dayjs from "dayjs";

import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Card from "react-bootstrap/Card";
import { FaDatabase } from "react-icons/fa";

import { Paginated } from "@/@types/generics/paginated";
import { ResumeIndicatorM2ByHealthTeamData } from "@/services/esus/indicator/emulti/m2/getResumeIndicatorM2ByHealthTeam";
import { getQuarterDates } from "@/utils/quarters";
import { exportExcel } from "@/utils/exports";
import { GenericObject } from "@/@types/generics/genericals";
import { getIndicatorClassification, IndicatorEmultiEnum } from "@/utils/indicatorsEmulti";
import { calculatePercentage } from "@/utils/calculatePercentage";

import { IndicatorEmultiM2HealthTeamsFilters } from "./IndicatorEmultiM2HealthTeamsFilters";
import { IndicatorEmultiM2HealthTeamsClassificationTable } from "./IndicatorEmultiM2HealthTeamsTable";
import { PaginationLinks } from "@/components/PaginationLinks";
import { NoRecordsFeedback } from "@/components/NoRecordsFeedback";
import { LoadingScreen } from "@/components/LoadingScreen";

type IndicatorEmultiM2HealthTeamsProps = {
	selectedQuarter: string;
	search: string;
	setSearch: (search: string) => void;
	isLoading: boolean;
	paginatedHealthTeamsClassifications: Paginated<ResumeIndicatorM2ByHealthTeamData>;
	pageNumber: number;
	setPageNumber: (pageNumber: number) => void;
	handleGetAllResumeIndicatorM2ByHealthTeam: () => Promise<ResumeIndicatorM2ByHealthTeamData[]>;
};

export function IndicatorEmultiM2HealthTeams({
	selectedQuarter,
	search,
	setSearch,
	isLoading,
	paginatedHealthTeamsClassifications,
	pageNumber,
	setPageNumber,
	handleGetAllResumeIndicatorM2ByHealthTeam,
}: Readonly<IndicatorEmultiM2HealthTeamsProps>) {
	const [isLoadingExportData, setIsLoadingExportData] = useState(false);

	async function handleExportTable() {
		setIsLoadingExportData(true);
		const healthTeamsClassifications = await handleGetAllResumeIndicatorM2ByHealthTeam();
		setIsLoadingExportData(false);
		const quarterDates = getQuarterDates(selectedQuarter);

		if (Object.keys(healthTeamsClassifications).length > 0) {
			const excelData: GenericObject[] = transformIndicatorM2AttendancesToGenericObjectArray(
				healthTeamsClassifications
			);
			exportExcel(
				excelData,
				`Ações por equipe - ${dayjs(quarterDates.startDate).format("DD-MM-YYYY")} à ${dayjs(
					quarterDates.endDate
				).format("DD/MM/YYYY")}`
			);
		}
	}

	function transformIndicatorM2AttendancesToGenericObjectArray(
		healthTeamClassifications: ResumeIndicatorM2ByHealthTeamData[]
	) {
		return healthTeamClassifications.map(
			(healthTeamClassification) =>
				({
					Equipe: healthTeamClassification.healthTeamName,
					"Equipe INE": healthTeamClassification.healthTeamIne,
					"Total de ações": healthTeamClassification.totalAttendances,
					"Ações compartilhadas": healthTeamClassification.totalSharedAttendances,
					"Ações não compartilhadas": healthTeamClassification.totalNonSharedAttendances,
					"Média de ações compartilhadas": `${calculatePercentage(
						healthTeamClassification.totalSharedAttendances / 100,
						healthTeamClassification.totalAttendances
					)}`,
					Classificação: getIndicatorClassification(
						IndicatorEmultiEnum.M2,
						calculatePercentage(
							healthTeamClassification.totalSharedAttendances / 100,
							healthTeamClassification.totalAttendances
						)
					),
				} as GenericObject)
		);
	}

	return (
		<Row className="bg-white m-3 p-2">
			<Col>
				<LoadingScreen loading={isLoadingExportData} />
				<Card>
					<Card.Header>
						<IndicatorEmultiM2HealthTeamsFilters
							search={search}
							setSearch={setSearch}
							handleExportTable={handleExportTable}
						/>
					</Card.Header>
					<Card.Body>
						<IndicatorEmultiM2HealthTeamsClassificationTable
							healthTeamsClassifications={paginatedHealthTeamsClassifications.data}
							isLoading={isLoading}
						/>
					</Card.Body>
					<Card.Footer>
						{paginatedHealthTeamsClassifications?.totalRecords ? (
							<PaginationLinks
								itemsPerPage={paginatedHealthTeamsClassifications.pageSize}
								totalPages={paginatedHealthTeamsClassifications.totalRecords}
								changeSelectedPage={(pageNumber) => {
									setPageNumber(pageNumber);
								}}
								pageNumber={pageNumber || 0}
							/>
						) : (
							!isLoading && (
								<NoRecordsFeedback
									message={"Nenhum resultado encontrado."}
									icon={<FaDatabase />}
								/>
							)
						)}
					</Card.Footer>
				</Card>
			</Col>
		</Row>
	);
}
